/**
 * Wrapper around Razorpay Checkout. Spec §12b. The checkout.js SDK is loaded on
 * demand (loadRazorpayScript) the first time a parent starts a payment, so its
 * SDK + ~120 payment-method chunks don't load on pages with no checkout.
 *
 * The checkout uses the platform key id (returned by /payments/create-order).
 * Parent fees are collected centrally on the platform's Razorpay account and the
 * academy is settled monthly; the key is never academy-specific.
 */

const CHECKOUT_SRC = 'https://checkout.razorpay.com/v1/checkout.js'

let loadPromise: Promise<void> | null = null

/**
 * Inject the Razorpay checkout.js script once. Idempotent: resolves immediately
 * if already loaded, and reuses the in-flight promise across concurrent calls.
 */
export function loadRazorpayScript(): Promise<void> {
  if (typeof window !== 'undefined' && window.Razorpay) return Promise.resolve()
  if (loadPromise) return loadPromise

  loadPromise = new Promise<void>((resolve, reject) => {
    const script = document.createElement('script')
    script.src = CHECKOUT_SRC
    script.async = true
    script.onload = () => resolve()
    script.onerror = () => {
      loadPromise = null // allow a retry on the next attempt
      reject(new RazorpayNotLoadedError())
    }
    document.head.appendChild(script)
  })
  return loadPromise
}

interface RazorpayInstance {
  open: () => void
  on: (event: 'payment.failed', handler: (resp: unknown) => void) => void
}

interface RazorpayConstructor {
  new (options: unknown): RazorpayInstance
}

declare global {
  interface Window {
    Razorpay?: RazorpayConstructor
  }
}

export interface RazorpayCheckoutSuccess {
  razorpay_payment_id: string
  razorpay_order_id: string
  razorpay_signature: string
}

export interface OpenCheckoutOptions {
  /** Academy's own Razorpay key id (returned by /payments/create-order). */
  keyId: string
  /** Order id from /payments/create-order. */
  orderId: string
  /** Amount in paise. */
  amountInr: number
  currency: 'INR'

  academyName: string
  description: string
  themeColor: string

  parentName?: string
  parentEmail?: string
  parentPhone?: string

  onSuccess: (response: RazorpayCheckoutSuccess) => void
  onError: (error: unknown) => void
  onDismiss?: () => void
}

export class RazorpayNotLoadedError extends Error {
  constructor() {
    super('Razorpay Checkout failed to load. Check the network connection and try again.')
    this.name = 'RazorpayNotLoadedError'
  }
}

export async function openRazorpayCheckout(opts: OpenCheckoutOptions): Promise<void> {
  await loadRazorpayScript()
  if (typeof window === 'undefined' || !window.Razorpay) {
    throw new RazorpayNotLoadedError()
  }

  const rzp = new window.Razorpay({
    key: opts.keyId,
    amount: opts.amountInr,
    currency: opts.currency,
    order_id: opts.orderId,
    name: opts.academyName,
    description: opts.description,
    prefill: {
      name: opts.parentName ?? '',
      email: opts.parentEmail ?? '',
      contact: opts.parentPhone ?? '',
    },
    theme: { color: opts.themeColor },
    modal: {
      ondismiss: opts.onDismiss,
    },
    handler: opts.onSuccess,
  })
  rzp.on('payment.failed', opts.onError)
  rzp.open()
}
